import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useJobsite } from "../context/JobsiteContext";
import { useAuth } from "../context/AuthContext";
import Sheet from "../components/Sheet";
import { compressImage } from "../lib/image";
import { openDocument, resolveFileUrl, fileKind, fmtSize } from "../lib/openDocument";
import { fmtDate } from "../lib/utils";
import {
  ArrowLeft,
  Camera,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ClipboardCheck,
  FileText,
  Filter,
  ImagePlus,
  Loader2,
  MinusCircle,
  MoreHorizontal,
  Paperclip,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  X,
  XCircle,
} from "lucide-react";

/* ---------- constantes e ajudantes ---------- */

const STATUSES = ["Draft", "In Progress", "In Review", "Closed"];
const STATUS_TONE = { Draft: "muted", "In Progress": "warn", "In Review": "info", Closed: "ok" };
const TONES = {
  muted: "border-border bg-muted text-muted-foreground",
  accent: "border-primary/40 bg-primary/10 text-primary",
  ok: "border-green-500/40 bg-green-500/10 text-green-500",
  warn: "border-amber-500/40 bg-amber-500/10 text-amber-500",
  info: "border-blue-500/40 bg-blue-500/10 text-blue-400",
  bad: "border-red-500/40 bg-red-500/10 text-red-400",
};
const CHECKPOINT_TONE = { "Hold Point": "bad", "Witness Point": "warn", "Review Point": "accent" };
const RESPONSE_TYPES = ["Pass/Fail", "Yes/No", "Text", "Number", "Date", "Signature", "Photo"];
const CHECKPOINT_TYPES = ["Hold Point", "Witness Point", "Review Point"];
const DOC_ACCEPT = ".pdf,.png,.jpg,.jpeg,.heic,.webp,.doc,.docx,.xls,.xlsx,.txt,.csv,image/*";

function errText(err, fallback) {
  const d = err && err.response && err.response.data && err.response.data.detail;
  return typeof d === "string" ? d : fallback;
}

function Chip({ tone = "muted", children }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-widest ${TONES[tone]}`}
    >
      {children}
    </span>
  );
}

function StatusChip({ status, big = false }) {
  const s = status || "Draft";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border font-semibold ${TONES[STATUS_TONE[s] || "muted"]} ${
        big ? "px-4 py-2 text-base" : "px-3 py-1 text-sm"
      }`}
    >
      {s}
    </span>
  );
}

function ProgressBar({ done, total }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-28 overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full bg-green-500" style={{ width: `${pct}%` }} />
      </div>
      <span className="font-mono text-xs text-muted-foreground">
        {done}/{total}
      </span>
    </div>
  );
}

function SectionLabel({ children }) {
  return <div className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">{children}</div>;
}

// Mesma regra do grafico da tela de computador: so conta itens de verdade (sem cabecalhos).
function computeBreakdown(results) {
  let total = 0;
  let inspected = 0;
  let pass = 0;
  let fail = 0;
  let na = 0;
  let open = 0;
  (results || []).forEach((r) => {
    const snap = r.item_snapshot || r;
    const itemType = snap.item_type || snap.type;
    const hasResponse = snap.response_type || snap.checkpoint_type || snap.acceptance_criteria;
    const isHeader = itemType === "Section Header" || itemType === "header" || (!hasResponse && r.children?.length > 0);
    if (isHeader) return;
    total += 1;
    const st = r.status_selection || "";
    const hasText = typeof r.text_value === "string" && r.text_value.trim().length > 0;
    const hasNum = r.numeric_value !== null && r.numeric_value !== undefined && String(r.numeric_value).trim() !== "";
    const hasDate = typeof r.date_value === "string" && r.date_value.trim().length > 0;
    const hasPhotos = Array.isArray(r.attachments) && r.attachments.length > 0;
    const answered = (st !== "" && st !== "Open") || hasText || hasNum || hasDate || hasPhotos;
    if (answered) {
      inspected += 1;
      if (st === "Pass" || st === "Yes") pass += 1;
      else if (st === "Fail" || st === "No") fail += 1;
      else if (st === "N/A") na += 1;
      else pass += 1;
    } else {
      open += 1;
    }
  });
  return { total, inspected, pass, fail, na, open };
}

// Monta a lista na ordem certa, com a numeracao 1, 1.1, 1.1.1 (igual a tela de computador).
function buildChecklist(rawResults) {
  const list = [...rawResults].sort((a, b) => (a.sort_index ?? 0) - (b.sort_index ?? 0));
  const byId = {};
  const roots = [];
  list.forEach((r) => {
    byId[r.id] = { ...r, children: [] };
  });
  list.forEach((r) => {
    const parentId = r.parent_result_id || r.parent_id || r.item_snapshot?.parent_id || r.item_snapshot?.parent_item_id;
    if (parentId && byId[parentId]) byId[parentId].children.push(byId[r.id]);
    else roots.push(byId[r.id]);
  });
  const out = [];
  const walk = (nodes, prefix = "") => {
    nodes.forEach((n, i) => {
      const num = prefix ? `${prefix}.${i + 1}` : `${i + 1}`;
      out.push({ node: n, depth: prefix ? prefix.split(".").length : 0, number: num });
      if (n.children.length > 0) walk(n.children, num);
    });
  };
  walk(roots);
  return out;
}

function isSectionHeader(r) {
  const snap = r.item_snapshot || r;
  const itemType = snap.item_type || snap.type;
  const hasResponse = snap.response_type || snap.checkpoint_type || snap.acceptance_criteria;
  const isHeader = itemType === "Section Header" || itemType === "header" || (!hasResponse && r.children?.length > 0);
  return isHeader && !snap.response_type;
}

/* ---------- entrada: lista ou detalhe, conforme o endereco ---------- */

export default function InspectionsMobile() {
  const params = useParams();
  const navigate = useNavigate();
  // Lista: /app/inspections. Detalhe: /app/inspections/:id. Usa so o :id. (O "*" da rota-mae /app/*
  // tambem aparece em useParams e, na lista, valeria "inspections", abrindo uma inspecao que nao existe.)
  const routeId = params.id || null;
  if (routeId) return <InspectionDetail key={routeId} id={routeId} onBack={() => navigate("/app/inspections")} />;
  return <InspectionList onOpen={(id) => navigate(`/app/inspections/${id}`)} />;
}

/* ---------- LISTA ---------- */

function InspectionList({ onOpen }) {
  const { activeSite } = useJobsite();
  const { user } = useAuth();
  const canCreate = user?.role === "admin" || user?.role === "supervisor";
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [filterOpen, setFilterOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [template, setTemplate] = useState(null);

  const siteId = activeSite ? activeSite.id : null;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/itp/inspections", {
        params: siteId ? { project_id: siteId, _: Date.now() } : { _: Date.now() },
        headers: { "Cache-Control": "no-cache" },
      });
      setItems(Array.isArray(data) ? data : []);
      setUpdatedAt(new Date());
    } catch (e) {
      toast.error("Could not load the inspections.");
    } finally {
      setLoaded(true);
    }
  }, [siteId]);

  useEffect(() => {
    setLoaded(false);
    load();
  }, [load]);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = items.filter((i) => {
      if (statusFilter !== "all" && (i.status || "Draft") !== statusFilter) return false;
      if (!q) return true;
      return [i.title, i.location_details, i.description, i.created_by_name].some((v) => (v || "").toLowerCase().includes(q));
    });
    const map = new Map();
    rows.forEach((i) => {
      const key = i.title || "Untitled";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(i);
    });
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([title, list]) => ({ title, list }));
  }, [items, query, statusFilter]);

  const filtered = statusFilter !== "all";

  return (
    <div className="mx-auto max-w-3xl px-4 pb-32 pt-5">
      <h1 className="text-3xl font-bold tracking-tight">Inspections</h1>

      <div className="mt-4 flex gap-2">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search"
            className="h-12 w-full rounded-lg border border-input bg-background pl-11 pr-3 text-base outline-none focus:border-primary"
            data-testid="insp-search"
          />
        </div>
        <button
          type="button"
          onClick={() => setFilterOpen(true)}
          className={`flex h-12 shrink-0 items-center gap-2 rounded-lg border px-4 font-semibold ${
            filtered ? "border-primary bg-primary/10 text-primary" : "border-border bg-card"
          }`}
          data-testid="insp-filter"
        >
          <Filter className="h-4 w-4" /> Filter
        </button>
      </div>

      <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
        <span>{updatedAt ? `Last updated ${updatedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "Loading..."}</span>
        <button type="button" onClick={load} className="inline-flex items-center gap-1 hover:text-foreground">
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      {loaded && groups.length === 0 && (
        <div className="grid place-items-center py-20 text-center">
          <div className="grid h-16 w-16 place-items-center rounded-full bg-muted text-muted-foreground">
            <ClipboardCheck className="h-8 w-8" />
          </div>
          <div className="mt-4 text-xl font-semibold">{items.length === 0 ? "No inspections yet" : "Nothing matches"}</div>
          <div className="mt-1 max-w-xs text-sm text-muted-foreground">
            {items.length === 0
              ? canCreate
                ? "Tap + to start an inspection from a template."
                : "Your supervisor will start the inspections for this project."
              : "Try another search or clear the filter."}
          </div>
        </div>
      )}

      <div className="mt-4 space-y-6">
        {groups.map((g) => (
          <div key={g.title}>
            <div className="mb-3 text-lg font-bold leading-snug">{g.title}</div>
            <div className="space-y-3">
              {g.list.map((i) => (
                <button
                  key={i.id}
                  type="button"
                  onClick={() => onOpen(i.id)}
                  className="block w-full rounded-xl border border-border bg-card p-4 text-left active:bg-accent"
                  data-testid={`insp-${i.id}`}
                >
                  <div className="text-lg font-bold leading-snug">{i.location_details || "No location"}</div>
                  {i.description && (
                    <div className="mt-1.5 text-sm">
                      <span className="font-semibold">Description: </span>
                      <span className="text-muted-foreground">{i.description}</span>
                    </div>
                  )}
                  <div className="mt-1 text-sm">
                    <span className="font-semibold">Created by: </span>
                    <span className="text-muted-foreground">{i.created_by_name || "--"}</span>
                  </div>
                  <div className="mt-1 text-sm">
                    <span className="font-semibold">Created: </span>
                    <span className="text-muted-foreground">{fmtDate(i.created_at)}</span>
                  </div>
                  {typeof i.items_count === "number" && i.items_count > 0 && (
                    <div className="mt-3">
                      <ProgressBar done={i.inspected_count || 0} total={i.items_count} />
                    </div>
                  )}
                  <div className="mt-3 flex items-center justify-between">
                    <StatusChip status={i.status} />
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {canCreate && (
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          aria-label="New inspection"
          className="fixed right-4 z-30 grid h-16 w-16 place-items-center rounded-2xl bg-primary text-primary-foreground shadow-xl active:scale-95"
          style={{ bottom: "calc(5.5rem + env(safe-area-inset-bottom))" }}
          data-testid="insp-new"
        >
          <Plus className="h-8 w-8" />
        </button>
      )}

      {filterOpen && (
        <Sheet
          title="Filter"
          onClose={() => setFilterOpen(false)}
          footer={
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setStatusFilter("all");
                  setFilterOpen(false);
                }}
                className="h-12 flex-1 rounded-xl border border-border text-base font-semibold"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => setFilterOpen(false)}
                className="h-12 flex-1 rounded-xl bg-primary text-base font-semibold text-primary-foreground"
              >
                Done
              </button>
            </div>
          }
        >
          <SectionLabel>Status</SectionLabel>
          <div className="mt-3 flex flex-wrap gap-2">
            {["all", ...STATUSES].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatusFilter(s)}
                className={`h-11 rounded-full border px-5 text-base font-medium ${
                  statusFilter === s ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground"
                }`}
              >
                {s === "all" ? "All" : s}
              </button>
            ))}
          </div>
        </Sheet>
      )}

      {pickerOpen && (
        <TemplatePicker
          onClose={() => setPickerOpen(false)}
          onPick={(t) => {
            setPickerOpen(false);
            setTemplate(t);
          }}
        />
      )}

      {template && (
        <NewInspectionSheet
          template={template}
          onClose={() => setTemplate(null)}
          onCreated={(id) => {
            setTemplate(null);
            onOpen(id);
          }}
        />
      )}
    </div>
  );
}

// "+": escolher o modelo de ITP, agrupado.
function TemplatePicker({ onClose, onPick }) {
  const [templates, setTemplates] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let cancelled = false;
    api
      .get("/itp/templates")
      .then(({ data }) => {
        if (!cancelled) setTemplates(Array.isArray(data) ? data.filter((t) => t.active !== false) : []);
      })
      .catch(() => {
        if (!cancelled) {
          setTemplates([]);
          toast.error("Could not load the templates.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = (templates || []).filter((t) => !q || (t.title || "").toLowerCase().includes(q) || (t.spec_section || "").toLowerCase().includes(q));
    const map = new Map();
    rows.forEach((t) => {
      const key = (t.spec_section || "").trim() || "Templates";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(t);
    });
    return [...map.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([name, list]) => ({ name, list: list.sort((a, b) => (a.title || "").localeCompare(b.title || "")) }));
  }, [templates, query]);

  return (
    <Sheet title="Inspection templates" onClose={onClose} wide>
      <div className="-mx-5 -my-5">
        <div className="px-5 pb-3 pt-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search"
              className="h-12 w-full rounded-lg border border-input bg-background pl-11 pr-3 text-base outline-none focus:border-primary"
            />
          </div>
        </div>

        {templates === null && <div className="py-12 text-center text-sm text-muted-foreground">Loading...</div>}
        {templates !== null && groups.length === 0 && (
          <div className="px-5 py-12 text-center text-sm text-muted-foreground">No templates found.</div>
        )}

        {groups.map((g) => (
          <div key={g.name}>
            <div className="bg-muted px-5 py-2.5 text-sm font-bold">{g.name}</div>
            <div className="divide-y divide-border">
              {g.list.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => onPick(t)}
                  className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left active:bg-accent"
                  data-testid={`tpl-${t.id}`}
                >
                  <span className="text-base">{t.title}</span>
                  <ChevronRight className="h-5 w-5 shrink-0 text-blue-400" />
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Sheet>
  );
}

// Dados da nova inspecao e criacao (clona o modelo para a obra).
function NewInspectionSheet({ template, onClose, onCreated }) {
  const { sites, activeSite } = useJobsite();
  const [projectId, setProjectId] = useState((activeSite && activeSite.id) || (sites[0] && sites[0].id) || "");
  const [location, setLocation] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!projectId) {
      toast.error("Choose a project.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/itp/templates/${template.id}/clone`,
        { project_id: projectId, location_details: description.trim() },
        { timeout: 90000 }
      );
      if (location.trim()) {
        await api.patch(`/itp/inspections/${data.inspection_id}`, { location_details: location.trim() });
      }
      toast.success("Inspection created");
      onCreated(data.inspection_id);
    } catch (err) {
      toast.error(errText(err, "Could not create the inspection."));
      setBusy(false);
    }
  };

  const field = "h-12 w-full rounded-lg border border-input bg-background px-3 text-base outline-none focus:border-primary";

  return (
    <Sheet
      title="New inspection"
      subtitle={template.title}
      onClose={() => !busy && onClose()}
      footer={
        <button
          type="button"
          onClick={create}
          disabled={busy}
          className="flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-primary text-base font-semibold text-primary-foreground disabled:opacity-60"
          data-testid="create-inspection"
        >
          {busy && <Loader2 className="h-5 w-5 animate-spin" />}
          {busy ? "Creating..." : "Create inspection"}
        </button>
      }
    >
      <div className="space-y-5">
        <div>
          <label htmlFor="new-project" className="mb-1.5 block text-sm font-medium">
            Project
          </label>
          <select id="new-project" value={projectId} onChange={(e) => setProjectId(e.target.value)} className={field}>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="new-location" className="mb-1.5 block text-sm font-medium">
            Location
          </label>
          <input id="new-location" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Level 11" className={field} />
        </div>
        <div>
          <label htmlFor="new-desc" className="mb-1.5 block text-sm font-medium">
            Description
          </label>
          <textarea
            id="new-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder="Optional"
            className="w-full resize-none rounded-lg border border-input bg-background px-3 py-3 text-base outline-none focus:border-primary"
          />
        </div>
        <p className="text-xs text-muted-foreground">
          The questions come from the template. The documents attached to the template appear in the inspection too.
        </p>
      </div>
    </Sheet>
  );
}

/* ---------- DETALHE ---------- */

function InspectionDetail({ id, onBack }) {
  const { user } = useAuth();
  const isStaff = user?.role === "admin" || user?.role === "supervisor";
  const isAdmin = user?.role === "admin";
  const [ins, setIns] = useState(null);
  const [failed, setFailed] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [hold, setHold] = useState(null);
  const [showGeneral, setShowGeneral] = useState(true);
  const [statusOpen, setStatusOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const [addDocOpen, setAddDocOpen] = useState(false);
  const [addItemOpen, setAddItemOpen] = useState(false);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get(`/itp/inspections/${id}`);
      setIns(data);
      setFailed(false);
      setLoadError("");
    } catch (e) {
      const code = e && e.response ? `HTTP ${e.response.status}: ` : "";
      setLoadError(code + errText(e, (e && e.message) || "No answer from the server"));
      setFailed(true);
      toast.error("Could not load the inspection.");
    }
  }, [id]);

  useEffect(() => {
    reload();
  }, [reload]);

  const results = useMemo(() => (ins ? ins.results || ins.items || ins.checklist || [] : []), [ins]);
  const breakdown = useMemo(() => computeBreakdown(results), [results]);
  const checklist = useMemo(() => buildChecklist(results), [results]);
  const status = (ins && ins.status) || "Draft";
  const isClosed = status === "Closed";
  const docs = (ins && ins.documents) || [];

  const changeStatus = useCallback(
    async (next, auto = false) => {
      try {
        await api.patch(`/itp/inspections/${id}`, { status: next });
        setIns((p) => (p ? { ...p, status: next } : p));
        if (!auto) toast.success(`Status: ${next}`);
      } catch (e) {
        toast.error(errText(e, "Could not change the status."));
      }
    },
    [id]
  );

  // Igual a tela de computador: responder o primeiro item passa de Draft para In Progress.
  useEffect(() => {
    if (!ins || !ins.id) return;
    if (breakdown.inspected > 0 && ins.status === "Draft") changeStatus("In Progress", true);
    else if (breakdown.inspected === 0 && ins.status === "In Progress") changeStatus("Draft", true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breakdown.inspected, ins && ins.status, ins && ins.id]);

  const updateResult = async (rid, patch) => {
    try {
      const { data } = await api.patch(`/itp/inspection-results/${rid}`, patch);
      setIns((p) => (p ? { ...p, results: (p.results || []).map((r) => (r.id === rid ? data : r)) } : p));
      setHold(null);
    } catch (e) {
      const detail = errText(e, "Could not save the answer.");
      if (e.response && e.response.status === 409) setHold({ rid, message: detail });
      toast.error(detail);
    }
  };

  const addPhoto = async (rid, file) => {
    try {
      const small = await compressImage(file);
      const form = new FormData();
      form.append("file", small);
      await api.post(`/itp/inspection-results/${rid}/attachments/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      await reload();
    } catch (e) {
      toast.error("The photo could not be uploaded. Check your connection and try again.");
    }
  };

  const removePhoto = async (rid, aid) => {
    try {
      await api.delete(`/itp/inspection-results/${rid}/attachments/${aid}`);
      await reload();
    } catch (e) {
      toast.error("Could not remove the photo.");
    }
  };

  const deleteInspection = async () => {
    if (!window.confirm("Delete this inspection? This cannot be undone.")) return;
    try {
      await api.delete(`/itp/inspections/${id}`);
      toast.success("Inspection deleted");
      onBack();
    } catch (e) {
      toast.error(errText(e, "Could not delete."));
    }
  };

  if (failed && !ins) {
    return (
      <div className="mx-auto max-w-3xl px-4 pt-5">
        <button type="button" onClick={onBack} className="flex items-center gap-1 text-base font-medium text-primary">
          <ArrowLeft className="h-5 w-5" /> Inspections
        </button>
        <div className="py-16 text-center">
          <div className="text-muted-foreground">This inspection could not be opened.</div>
          {loadError && <div className="mt-2 break-words px-4 font-mono text-[11px] text-muted-foreground">Details: {loadError}</div>}
          <button
            type="button"
            onClick={reload}
            className="mt-5 inline-flex h-12 items-center gap-2 rounded-lg border border-border px-5 font-semibold active:bg-accent"
            data-testid="insp-retry"
          >
            <RefreshCw className="h-4 w-4" /> Try again
          </button>
        </div>
      </div>
    );
  }
  if (!ins) {
    return (
      <div className="mx-auto max-w-3xl px-4 pt-5">
        <button type="button" onClick={onBack} className="flex items-center gap-1 text-base font-medium text-primary">
          <ArrowLeft className="h-5 w-5" /> Inspections
        </button>
        <div className="grid place-items-center py-24 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </div>
    );
  }

  const title = ins.title || ins.template_snapshot?.title || "Untitled inspection";
  const revision = ins.revision || ins.template_snapshot?.revision || 1;

  return (
    <div className="mx-auto max-w-3xl px-4 pb-32 pt-4">
      {/* Barra de cima */}
      <div className="flex items-center justify-between">
        <button type="button" onClick={onBack} className="flex h-11 items-center gap-1 pr-3 text-base font-medium text-primary" data-testid="insp-back">
          <ArrowLeft className="h-5 w-5" /> Inspections
        </button>
        <div className="flex items-center gap-1">
          {isStaff && (
            <button
              type="button"
              onClick={() => setEditOpen(true)}
              className="flex h-11 items-center gap-1.5 rounded-lg px-3 text-base font-medium active:bg-accent"
              data-testid="insp-edit"
            >
              <Pencil className="h-4 w-4" /> Edit
            </button>
          )}
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-label="More"
            className="grid h-11 w-11 place-items-center rounded-lg active:bg-accent"
          >
            <MoreHorizontal className="h-6 w-6" />
          </button>
        </div>
      </div>

      {/* Titulo */}
      <div className="mt-4">
        <div className="text-sm text-muted-foreground">Inspections</div>
        <h1 className="mt-1 text-3xl font-bold leading-tight tracking-tight">
          {ins.location_details ? `${ins.location_details} - ` : ""}
          {title}
        </h1>
        <div className="mt-3 space-y-1 text-base">
          <div>
            <span className="font-semibold">Project: </span>
            <span className="text-muted-foreground">{ins.project_name || "--"}</span>
          </div>
          <div>
            <span className="font-semibold">Location: </span>
            <span className="text-muted-foreground">{ins.location_details || "--"}</span>
          </div>
          <div>
            <span className="font-semibold">Revision: </span>
            <span className="text-muted-foreground">{revision}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setStatusOpen(true)}
          className="mt-3 inline-flex items-center gap-2 active:opacity-80"
          data-testid="insp-status"
        >
          <StatusChip status={status} big />
          <ChevronDown className="h-5 w-5 text-muted-foreground" />
        </button>
      </div>

      {/* Descricao */}
      {ins.description && (
        <div className="mt-5 border-t border-border pt-5">
          <div className="text-lg font-bold">Description</div>
          <p className="mt-1 text-base leading-relaxed text-muted-foreground">{ins.description}</p>
        </div>
      )}

      {/* Anexos */}
      <div className="mt-5 border-t border-border pt-5">
        <div className="flex items-center justify-between">
          <div className="text-lg font-bold">Attachments ({docs.length})</div>
          <div className="flex items-center gap-4">
            {isStaff && (
              <button type="button" onClick={() => setAddDocOpen(true)} className="text-base font-semibold text-primary" data-testid="add-doc">
                Add
              </button>
            )}
            {docs.length > 0 && (
              <button type="button" onClick={() => setDocsOpen(true)} className="text-base font-semibold text-blue-400" data-testid="see-all-docs">
                See all
              </button>
            )}
          </div>
        </div>
        {docs.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">
            {isStaff ? "No documents yet. Tap Add to attach data sheets or drawings." : "No documents were attached to this inspection."}
          </p>
        ) : (
          <div className="-mx-4 mt-3 flex snap-x gap-3 overflow-x-auto px-4 pb-1">
            {docs.map((d) => (
              <DocCard key={d.id} doc={d} />
            ))}
          </div>
        )}
      </div>

      {/* Informacoes gerais */}
      <div className="mt-5 border-t border-border pt-5">
        <div className="flex items-center justify-between">
          <div className="text-lg font-bold">General information</div>
          <button
            type="button"
            onClick={() => setShowGeneral((v) => !v)}
            className="flex items-center gap-1 text-sm font-bold uppercase tracking-wide text-blue-400"
          >
            {showGeneral ? "Hide" : "Show"} {showGeneral ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
        {showGeneral && (
          <div className="mt-3 divide-y divide-border rounded-xl border border-border bg-card">
            {[
              ["Template", `${title} (Rev ${revision})`],
              ["Created by", ins.created_by_name || "--"],
              ["Created", fmtDate(ins.created_at)],
              ["Last updated", ins.updated_at ? fmtDate(ins.updated_at) : "--"],
            ].map(([k, v]) => (
              <div key={k} className="px-4 py-3">
                <div className="text-sm font-semibold">{k}</div>
                <div className="text-base text-muted-foreground">{v}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Progresso */}
      <div className="mt-6 rounded-xl border border-border bg-card p-4">
        <div className="flex items-end justify-between">
          <div className="text-lg font-bold">Items</div>
          <div className="font-mono text-sm text-muted-foreground">
            {breakdown.inspected}/{breakdown.total} inspected
          </div>
        </div>
        <div className="mt-3 flex h-2.5 overflow-hidden rounded-full bg-muted">
          {breakdown.total > 0 && (
            <>
              <div className="h-full bg-green-500" style={{ width: `${(breakdown.pass / breakdown.total) * 100}%` }} />
              <div className="h-full bg-red-500" style={{ width: `${(breakdown.fail / breakdown.total) * 100}%` }} />
              <div className="h-full bg-zinc-500" style={{ width: `${(breakdown.na / breakdown.total) * 100}%` }} />
            </>
          )}
        </div>
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 font-mono text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-green-500" /> Pass {breakdown.pass}</span>
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-red-500" /> Fail {breakdown.fail}</span>
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-zinc-500" /> N/A {breakdown.na}</span>
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-border" /> Open {breakdown.open}</span>
        </div>
      </div>

      {isClosed && (
        <div className="mt-3 rounded-lg border border-green-500/40 bg-green-500/10 p-3 text-sm text-green-500">
          This inspection is closed. The answers can no longer be changed.
        </div>
      )}

      {/* Perguntas */}
      <div className="mt-4 space-y-3">
        {checklist.length === 0 && <div className="py-10 text-center text-sm text-muted-foreground">This inspection has no items.</div>}
        {checklist.map(({ node, depth, number }) =>
          isSectionHeader(node) ? (
            <SectionHeader key={node.id} node={node} depth={depth} number={number} />
          ) : (
            <ItemCard
              key={node.id}
              result={node}
              number={number}
              depth={depth}
              disabled={isClosed}
              holdMessage={hold && hold.rid === node.id ? hold.message : null}
              onUpdate={(patch) => updateResult(node.id, patch)}
              onAddPhoto={(file) => addPhoto(node.id, file)}
              onRemovePhoto={(aid) => removePhoto(node.id, aid)}
            />
          )
        )}
      </div>

      {isAdmin && !isClosed && (
        <button
          type="button"
          onClick={() => setAddItemOpen(true)}
          className="mt-4 flex h-14 w-full items-center justify-center gap-2 rounded-xl border border-dashed border-border text-base font-semibold"
          data-testid="add-item"
        >
          <Plus className="h-5 w-5" /> Add item
        </button>
      )}

      {/* Folhas */}
      {statusOpen && (
        <Sheet title="Status" onClose={() => setStatusOpen(false)}>
          <div className="-mx-5 -my-5 divide-y divide-border">
            {STATUSES.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => {
                  setStatusOpen(false);
                  changeStatus(s);
                }}
                className="flex w-full items-center justify-between px-5 py-4 text-left active:bg-accent"
                data-testid={`status-${s}`}
              >
                <StatusChip status={s} />
                {s === status && <CheckCircle2 className="h-5 w-5 text-primary" />}
              </button>
            ))}
          </div>
        </Sheet>
      )}

      {menuOpen && (
        <Sheet title="Inspection" onClose={() => setMenuOpen(false)}>
          <div className="-mx-5 -my-5 divide-y divide-border">
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                reload();
              }}
              className="flex w-full items-center gap-3 px-5 py-4 text-left text-base active:bg-accent"
            >
              <RefreshCw className="h-5 w-5" /> Refresh
            </button>
            {isAdmin && (
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  deleteInspection();
                }}
                className="flex w-full items-center gap-3 px-5 py-4 text-left text-base text-red-400 active:bg-accent"
                data-testid="insp-delete"
              >
                <Trash2 className="h-5 w-5" /> Delete inspection
              </button>
            )}
          </div>
        </Sheet>
      )}

      {editOpen && (
        <EditDetailsSheet
          ins={ins}
          onClose={() => setEditOpen(false)}
          onSaved={async () => {
            setEditOpen(false);
            await reload();
          }}
        />
      )}

      {docsOpen && (
        <DocsSheet
          docs={docs}
          isStaff={isStaff}
          onClose={() => setDocsOpen(false)}
          onAdd={() => {
            setDocsOpen(false);
            setAddDocOpen(true);
          }}
          onDeleted={reload}
        />
      )}

      {addDocOpen && (
        <AddDocSheet
          inspectionId={ins.id}
          onClose={() => setAddDocOpen(false)}
          onDone={async () => {
            setAddDocOpen(false);
            await reload();
          }}
        />
      )}

      {addItemOpen && (
        <AddItemSheet
          inspectionId={ins.id}
          onClose={() => setAddItemOpen(false)}
          onDone={async () => {
            setAddItemOpen(false);
            await reload();
          }}
        />
      )}
    </div>
  );
}

/* ---------- anexos (documentos) ---------- */

function DocThumb({ doc, size = "lg" }) {
  const kind = fileKind(doc.name);
  const box = size === "lg" ? "h-24" : "h-12 w-12";
  if (kind === "image") {
    return <img src={resolveFileUrl(doc.url)} alt="" className={`${box} w-full rounded-t-lg object-cover`} />;
  }
  const color = kind === "pdf" ? "text-red-400" : kind === "sheet" ? "text-green-400" : "text-blue-400";
  return (
    <div className={`grid ${box} w-full place-items-center bg-muted ${size === "lg" ? "rounded-t-lg" : "rounded-lg"}`}>
      <FileText className={`${size === "lg" ? "h-10 w-10" : "h-6 w-6"} ${color}`} />
    </div>
  );
}

function DocCard({ doc }) {
  return (
    <button
      type="button"
      onClick={() => openDocument(doc.url)}
      className="w-40 shrink-0 snap-start overflow-hidden rounded-xl border border-border bg-card text-left active:bg-accent"
      data-testid={`doc-${doc.id}`}
    >
      <DocThumb doc={doc} />
      <div className="line-clamp-2 break-words px-3 py-2 text-sm font-medium leading-snug">{doc.name}</div>
    </button>
  );
}

function DocsSheet({ docs, isStaff, onClose, onAdd, onDeleted }) {
  const [busyId, setBusyId] = useState(null);

  const remove = async (doc) => {
    const where = doc.scope === "template" ? "all inspections of this ITP" : "this inspection";
    if (!window.confirm(`Remove "${doc.name}" from ${where}?`)) return;
    setBusyId(doc.id);
    try {
      await api.delete(`/itp/documents/${doc.id}`);
      toast.success("Document removed");
      await onDeleted();
    } catch (e) {
      toast.error(errText(e, "Could not remove the document."));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Sheet
      title={`Attachments (${docs.length})`}
      onClose={onClose}
      footer={
        isStaff ? (
          <button
            type="button"
            onClick={onAdd}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-primary text-base font-semibold text-primary-foreground"
          >
            <Plus className="h-5 w-5" /> Add document
          </button>
        ) : null
      }
    >
      <div className="-mx-5 -my-5 divide-y divide-border">
        {docs.length === 0 && <div className="px-5 py-10 text-center text-sm text-muted-foreground">No documents.</div>}
        {docs.map((d) => (
          <div key={d.id} className="flex items-center gap-3 px-5 py-3">
            <button type="button" onClick={() => openDocument(d.url)} className="flex min-w-0 flex-1 items-center gap-3 text-left active:opacity-70">
              <div className="w-12 shrink-0 overflow-hidden rounded-lg">
                <DocThumb doc={d} size="sm" />
              </div>
              <div className="min-w-0">
                <div className="line-clamp-2 break-words text-base font-medium leading-snug">{d.name}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {d.scope === "template" ? "All inspections of this ITP" : "This inspection"}
                  {d.size ? ` | ${fmtSize(d.size)}` : ""}
                  {d.uploaded_by ? ` | ${d.uploaded_by}` : ""}
                </div>
              </div>
            </button>
            {isStaff && (
              <button
                type="button"
                onClick={() => remove(d)}
                disabled={busyId === d.id}
                aria-label="Remove document"
                className="grid h-11 w-11 shrink-0 place-items-center rounded-lg text-red-400 active:bg-accent disabled:opacity-50"
              >
                {busyId === d.id ? <Loader2 className="h-5 w-5 animate-spin" /> : <Trash2 className="h-5 w-5" />}
              </button>
            )}
          </div>
        ))}
      </div>
    </Sheet>
  );
}

function AddDocSheet({ inspectionId, onClose, onDone }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [scope, setScope] = useState("template");
  const [busy, setBusy] = useState(false);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/itp/inspections/${inspectionId}/documents`, form, {
        params: { scope },
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      toast.success("Document added");
      await onDone();
    } catch (e) {
      toast.error(errText(e, "Could not upload the document."));
      setBusy(false);
    }
  };

  const options = [
    { id: "template", title: "All inspections of this ITP", body: "Best for data sheets and drawings that apply to every inspection of this template." },
    { id: "inspection", title: "Only this inspection", body: "Only people who open this inspection will see it." },
  ];

  return (
    <Sheet
      title="Add document"
      onClose={() => !busy && onClose()}
      footer={
        <button
          type="button"
          onClick={upload}
          disabled={!file || busy}
          className="flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-primary text-base font-semibold text-primary-foreground disabled:opacity-50"
          data-testid="upload-doc"
        >
          {busy && <Loader2 className="h-5 w-5 animate-spin" />}
          {busy ? "Uploading..." : "Upload"}
        </button>
      }
    >
      <div className="space-y-5">
        <input
          ref={inputRef}
          type="file"
          accept={DOC_ACCEPT}
          className="hidden"
          onChange={(e) => setFile((e.target.files && e.target.files[0]) || null)}
        />
        <button
          type="button"
          onClick={() => inputRef.current && inputRef.current.click()}
          disabled={busy}
          className="flex min-h-[72px] w-full items-center gap-3 rounded-xl border border-dashed border-border px-4 text-left active:bg-accent"
        >
          <Paperclip className="h-6 w-6 shrink-0 text-muted-foreground" />
          <span className="min-w-0 break-words text-base">{file ? file.name : "Choose a file (PDF, image, Word, Excel)"}</span>
        </button>

        <div>
          <SectionLabel>Who sees it</SectionLabel>
          <div className="mt-3 space-y-2">
            {options.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => setScope(o.id)}
                disabled={busy}
                className={`block w-full rounded-xl border p-4 text-left ${
                  scope === o.id ? "border-primary bg-primary/10" : "border-border"
                }`}
                data-testid={`scope-${o.id}`}
              >
                <div className="text-base font-semibold">{o.title}</div>
                <div className="mt-0.5 text-sm text-muted-foreground">{o.body}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </Sheet>
  );
}

/* ---------- editar detalhes e adicionar item ---------- */

function EditDetailsSheet({ ins, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: ins.title || "",
    location_details: ins.location_details || "",
    description: ins.description || "",
  });
  const [busy, setBusy] = useState(false);
  const field = "h-12 w-full rounded-lg border border-input bg-background px-3 text-base outline-none focus:border-primary";

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/itp/inspections/${ins.id}`, form);
      toast.success("Details updated");
      await onSaved();
    } catch (e) {
      toast.error(errText(e, "Could not save."));
      setBusy(false);
    }
  };

  return (
    <Sheet
      title="Edit details"
      onClose={() => !busy && onClose()}
      footer={
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-primary text-base font-semibold text-primary-foreground disabled:opacity-60"
        >
          {busy && <Loader2 className="h-5 w-5 animate-spin" />} Save
        </button>
      }
    >
      <div className="space-y-5">
        <div>
          <label htmlFor="ed-title" className="mb-1.5 block text-sm font-medium">Title</label>
          <input id="ed-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={field} />
        </div>
        <div>
          <label htmlFor="ed-loc" className="mb-1.5 block text-sm font-medium">Location</label>
          <input id="ed-loc" value={form.location_details} onChange={(e) => setForm({ ...form, location_details: e.target.value })} className={field} />
        </div>
        <div>
          <label htmlFor="ed-desc" className="mb-1.5 block text-sm font-medium">Description</label>
          <textarea
            id="ed-desc"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={4}
            className="w-full resize-none rounded-lg border border-input bg-background px-3 py-3 text-base outline-none focus:border-primary"
          />
        </div>
      </div>
    </Sheet>
  );
}

function AddItemSheet({ inspectionId, onClose, onDone }) {
  const [title, setTitle] = useState("");
  const [checkpoint, setCheckpoint] = useState("");
  const [response, setResponse] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!title.trim()) {
      toast.error("Add a title.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/itp/inspections/${inspectionId}/results`, {
        title: title.trim(),
        checkpoint_type: checkpoint || null,
        response_type: response || null,
      });
      toast.success("Item added");
      await onDone();
    } catch (e) {
      toast.error(errText(e, "Could not add the item."));
      setBusy(false);
    }
  };

  const chips = (list, value, set) => (
    <div className="mt-2 flex flex-wrap gap-2">
      {list.map((o) => (
        <button
          key={o}
          type="button"
          onClick={() => set(value === o ? "" : o)}
          className={`h-11 rounded-full border px-4 text-base ${
            value === o ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground"
          }`}
        >
          {o}
        </button>
      ))}
    </div>
  );

  return (
    <Sheet
      title="Add item"
      onClose={() => !busy && onClose()}
      footer={
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-primary text-base font-semibold text-primary-foreground disabled:opacity-60"
        >
          {busy && <Loader2 className="h-5 w-5 animate-spin" />} Add item
        </button>
      }
    >
      <div className="space-y-5">
        <div>
          <label htmlFor="ai-title" className="mb-1.5 block text-sm font-medium">Question</label>
          <input
            id="ai-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="h-12 w-full rounded-lg border border-input bg-background px-3 text-base outline-none focus:border-primary"
          />
        </div>
        <div>
          <SectionLabel>Checkpoint type</SectionLabel>
          {chips(CHECKPOINT_TYPES, checkpoint, setCheckpoint)}
        </div>
        <div>
          <SectionLabel>Answer type</SectionLabel>
          {chips(RESPONSE_TYPES, response, setResponse)}
        </div>
      </div>
    </Sheet>
  );
}

/* ---------- perguntas ---------- */

function SectionHeader({ node, depth, number }) {
  const snap = node.item_snapshot || node;
  const title = snap.title || snap.activity_description || snap.name || "Section";
  return (
    <div className="mt-5 rounded-xl border-l-4 border-primary bg-primary/10 p-4" style={{ marginLeft: `${Math.min(depth, 2) * 12}px` }}>
      <div className="flex items-baseline gap-3">
        <span className="font-mono text-sm font-bold text-primary">{number}.</span>
        <div className="min-w-0">
          <div className="text-lg font-bold leading-snug">{title}</div>
          {snap.description && <div className="mt-0.5 text-sm text-muted-foreground">{snap.description}</div>}
        </div>
      </div>
    </div>
  );
}

function ChoiceButton({ label, icon: Icon, tone, active, disabled, onClick }) {
  const on = {
    pass: "border-green-500 bg-green-500 text-white",
    fail: "border-red-500 bg-red-500 text-white",
    na: "border-zinc-500 bg-zinc-500 text-white",
  }[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={`flex h-16 flex-col items-center justify-center gap-1 rounded-xl border-2 text-sm font-bold uppercase tracking-wide transition-colors disabled:opacity-40 ${
        active ? on : "border-border text-muted-foreground active:bg-accent"
      }`}
      data-testid={`choice-${label}`}
    >
      <Icon className="h-5 w-5" />
      {label}
    </button>
  );
}

function ItemCard({ result, number, depth, disabled, holdMessage, onUpdate, onAddPhoto, onRemovePhoto }) {
  const snap = result.item_snapshot || result;
  const rt = snap.response_type;
  const [comments, setComments] = useState(result.comments || "");
  const [textValue, setTextValue] = useState(result.text_value || "");
  const [numericValue, setNumericValue] = useState(result.numeric_value ?? "");
  const [dateValue, setDateValue] = useState(result.date_value || "");

  useEffect(() => {
    setComments(result.comments || "");
    setTextValue(result.text_value || "");
    setNumericValue(result.numeric_value ?? "");
    setDateValue(result.date_value || "");
  }, [result]);

  const sel = result.status_selection || "";
  const negative = sel === "Fail" || sel === "No";
  const isPhotoType = !["Pass/Fail", "Yes/No", "Number", "Date", "Signature", "Text"].includes(rt);
  const title = snap.title || snap.activity_description || snap.name || "Untitled item";

  const commit = (patch) => onUpdate({ comments, ...patch });
  const choose = (option) => {
    if (disabled) return;
    commit({ status_selection: sel === option ? "" : option });
  };

  const inputClass = "h-12 w-full rounded-lg border border-input bg-background px-3 text-base outline-none focus:border-primary disabled:opacity-50";

  return (
    <div
      className={`rounded-xl border bg-card p-4 ${negative ? "border-red-500/50" : "border-border"}`}
      style={{ marginLeft: `${Math.min(depth, 2) * 12}px` }}
      data-testid={`item-${result.id}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-sm font-bold text-primary">{number}.</span>
        {snap.checkpoint_type && <Chip tone={CHECKPOINT_TONE[snap.checkpoint_type] || "muted"}>{snap.checkpoint_type}</Chip>}
        {rt && <Chip>{rt}</Chip>}
      </div>
      <div className="mt-2 text-base font-semibold leading-snug">{title}</div>
      {snap.description && <div className="mt-1 text-sm text-muted-foreground">{snap.description}</div>}
      {snap.acceptance_criteria && (
        <div className="mt-2 rounded-lg bg-muted/60 px-3 py-2 text-sm text-muted-foreground">
          <span className="mr-1 font-mono text-[10px] font-bold uppercase tracking-widest">Acceptance</span>
          {snap.acceptance_criteria}
        </div>
      )}

      <div className="mt-4">
        {(rt === "Pass/Fail" || rt === "Yes/No") && (
          <div className="grid grid-cols-3 gap-2">
            {(rt === "Pass/Fail" ? ["Pass", "Fail"] : ["Yes", "No"]).map((o) => (
              <ChoiceButton
                key={o}
                label={o}
                icon={o === "Pass" || o === "Yes" ? CheckCircle2 : XCircle}
                tone={o === "Pass" || o === "Yes" ? "pass" : "fail"}
                active={sel === o}
                disabled={disabled}
                onClick={() => choose(o)}
              />
            ))}
            <ChoiceButton label="N/A" icon={MinusCircle} tone="na" active={sel === "N/A"} disabled={disabled} onClick={() => choose("N/A")} />
          </div>
        )}

        {rt === "Number" && (
          <input
            type="number"
            inputMode="decimal"
            value={numericValue}
            onChange={(e) => setNumericValue(e.target.value)}
            onBlur={() => commit({ numeric_value: numericValue === "" ? null : Number(numericValue) })}
            disabled={disabled}
            className={inputClass}
          />
        )}

        {rt === "Date" && (
          <input
            type="date"
            value={(dateValue || "").slice(0, 10)}
            onChange={(e) => {
              setDateValue(e.target.value);
              if (e.target.value) commit({ date_value: e.target.value });
            }}
            disabled={disabled}
            className={inputClass}
          />
        )}

        {rt === "Text" && (
          <textarea
            value={textValue}
            onChange={(e) => setTextValue(e.target.value)}
            onBlur={() => textValue !== (result.text_value || "") && commit({ text_value: textValue })}
            rows={3}
            placeholder="Field notes"
            disabled={disabled}
            className="w-full resize-none rounded-lg border border-input bg-background px-3 py-3 text-base outline-none focus:border-primary disabled:opacity-50"
          />
        )}

        {rt === "Signature" && <SignaturePad value={result.text_value} disabled={disabled} onSave={(dataUrl) => commit({ text_value: dataUrl })} />}

        {isPhotoType && <PhotoField result={result} disabled={disabled} onAdd={onAddPhoto} onRemove={onRemovePhoto} />}
      </div>

      {isPhotoType && (
        <div className="mt-3">
          <SectionLabel>Comments</SectionLabel>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            onBlur={() => comments !== (result.comments || "") && onUpdate({ comments })}
            rows={2}
            placeholder="Write a comment..."
            disabled={disabled}
            className="mt-1.5 w-full resize-none rounded-lg border border-input bg-background px-3 py-3 text-base outline-none focus:border-primary disabled:opacity-50"
          />
        </div>
      )}

      {holdMessage && (
        <div className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-400" role="alert">
          {holdMessage}
        </div>
      )}
      {result.inspected_by_name && sel !== "" && (
        <div className="mt-3 text-xs text-muted-foreground">Answered by {result.inspected_by_name}</div>
      )}
    </div>
  );
}

function PhotoField({ result, disabled, onAdd, onRemove }) {
  const camRef = useRef(null);
  const libRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const handle = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) {
        await onAdd(f);
      }
    } finally {
      setUploading(false);
    }
  };

  const atts = result.attachments || [];
  return (
    <div className="space-y-3">
      {atts.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {atts.map((a) => (
            <div key={a.id} className="relative aspect-square overflow-hidden rounded-lg border border-border bg-background">
              <img
                src={resolveFileUrl(a.url)}
                alt="Attachment"
                className="h-full w-full object-cover"
                onClick={() => setLightbox(a.url)}
              />
              {!disabled && (
                <button
                  type="button"
                  onClick={() => window.confirm("Remove this photo?") && onRemove(a.id)}
                  aria-label="Remove photo"
                  className="absolute right-1 top-1 grid h-8 w-8 place-items-center rounded-full bg-black/70 text-white"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {!disabled && (
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => camRef.current && camRef.current.click()}
            disabled={uploading}
            className="flex h-14 items-center justify-center gap-2 rounded-xl border border-border bg-background text-base font-semibold active:bg-accent disabled:opacity-60"
            data-testid="take-photo"
          >
            {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />} Take photo
          </button>
          <button
            type="button"
            onClick={() => libRef.current && libRef.current.click()}
            disabled={uploading}
            className="flex h-14 items-center justify-center gap-2 rounded-xl border border-border bg-background text-base font-semibold active:bg-accent disabled:opacity-60"
          >
            <ImagePlus className="h-5 w-5" /> Library
          </button>
        </div>
      )}
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handle} />
      <input ref={libRef} type="file" accept="image/*" multiple className="hidden" onChange={handle} />

      {lightbox && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 p-3" onClick={() => setLightbox(null)}>
          <button
            type="button"
            onClick={() => setLightbox(null)}
            aria-label="Close"
            className="absolute right-4 top-4 grid h-11 w-11 place-items-center rounded-full bg-white/15 text-white"
            style={{ top: "calc(1rem + env(safe-area-inset-top))" }}
          >
            <X className="h-6 w-6" />
          </button>
          <img
            src={resolveFileUrl(lightbox)}
            alt="Full size"
            className="max-h-full max-w-full rounded-lg object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

// Assinatura com o dedo. Guarda a imagem (PNG) no mesmo campo que a tela de computador usa.
function SignaturePad({ value, disabled, onSave }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  const paintWhite = (canvas) => {
    const ctx = canvas.getContext("2d");
    ctx.save();
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    paintWhite(canvas);
    if (value && value.startsWith("data:image")) {
      const img = new Image();
      img.onload = () => {
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        setHasInk(true);
      };
      img.src = value;
    } else {
      setHasInk(false);
    }
  }, [value]);

  const point = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * canvas.width,
      y: ((e.clientY - rect.top) / rect.height) * canvas.height,
    };
  };

  const down = (e) => {
    if (disabled) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const p = point(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    // um toque simples vira um ponto
    ctx.lineTo(p.x + 0.1, p.y + 0.1);
    ctx.strokeStyle = "#000000";
    ctx.lineWidth = 3.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.stroke();
    setHasInk(true);
  };

  const move = (e) => {
    if (!drawing.current || disabled) return;
    const ctx = canvasRef.current.getContext("2d");
    const p = point(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  };

  const up = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onSave(canvasRef.current.toDataURL("image/png"));
  };

  const clear = () => {
    if (disabled) return;
    paintWhite(canvasRef.current);
    setHasInk(false);
    onSave("");
  };

  return (
    <div className="space-y-2">
      <div className="relative overflow-hidden rounded-xl border border-border bg-white">
        <canvas
          ref={canvasRef}
          width={640}
          height={240}
          className="block h-auto w-full touch-none"
          style={{ touchAction: "none" }}
          onPointerDown={down}
          onPointerMove={move}
          onPointerUp={up}
          onPointerCancel={up}
        />
        {!hasInk && !disabled && (
          <div className="pointer-events-none absolute inset-0 grid place-items-center text-base text-zinc-400">Sign here</div>
        )}
      </div>
      {!disabled && hasInk && (
        <button type="button" onClick={clear} className="h-11 rounded-lg border border-border px-4 text-sm font-semibold active:bg-accent">
          Clear signature
        </button>
      )}
    </div>
  );
}
